import { Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ClipboardService } from 'ngx-clipboard';
import { ToastrService } from 'ngx-toastr';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, Subject } from 'rxjs';
import { ITransactionDetails } from '../../Helper/helper';
import { canAccessVerifiedUserApis } from '../../Helper/common-utils';
import { AccountService } from '../../services/account.service';
import { CommonService } from '../../services/common.service';
import { HeightService } from '../../services/height.service';
import { TransactionService } from '../../services/transaction.service';
import { WebStorageService } from '../../services/web-storage.service';
import { getExplorerBaseUrl } from '../../utils/explorer-url.util';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { TransactionDetailsModalComponent } from '../transaction-details-modal/transaction-details-modal.component';
import { ReadableDatePipe } from '../../pipes/readable-date.pipe';
import { UiTerminologyPipe } from '../../pipes/ui-terminology.pipe';
import { ImageFallbackDirective } from '../../directives/image-fallback.directive';
import { PinataThumbnailPipe } from 'src/app/shared/pipes/pinata-thumbnail.pipe';

/** Shown when a transaction's asset has no media, and when it has no asset at all. */
const DEFAULT_TRANSACTION_THUMBNAIL = 'assets/images/default-nft.svg';

/**
 * Rows per page. Was hard-coded to 50, which on a throttled connection meant a large
 * payload before anything rendered; infinite scroll fetches the rest as needed.
 */
const TRANSACTIONS_PAGE_LIMIT = 20;

/**
 * File extension of an asset's renderable media, used to pick the <img> vs embed branch.
 *
 * The three call sites previously inlined this, and each dereferenced `primary_media`
 * unguarded — a log row whose asset has neither preview_image nor primary_media threw
 * and blanked the whole transaction list.
 */
function fileTypeOf(nft: any): string {
  const media = nft?.preview_image || nft?.primary_media || nft?.secondary_media?.[0];
  if (typeof media !== 'string' || !media.includes('.')) {
    return '';
  }
  return media.split('.').pop() ?? '';
}
@Component({
    selector: 'app-contract-transactions',
    templateUrl: './contract-transactions.component.html',
    styleUrls: ['./contract-transactions.component.css'],
    imports: [PinataThumbnailPipe, ImageFallbackDirective, NgIf, NgClass, FormsModule, ReactiveFormsModule, InfiniteScrollDirective, NgFor, ModalDirective, TransactionDetailsModalComponent, ReadableDatePipe, UiTerminologyPipe]
})
export class ContractTransactionsComponent implements OnInit, OnDestroy {
  @ViewChild('announcementContainer', { static: false }) announcementContainer: ElementRef | undefined;
  dynamicHeight: number = 0;
  @ViewChild('transactionDetailsModal', { static: false }) transactionDetailsModal?: ModalDirective;

  showtransactiondetail: boolean = false;
  account: any;
  user: any;
  regulated = true;
  transactions: any = [];
  transaction: any = {};
  transhUrl: string = '';
  private commonServiceSubscription: any;
  transactionData: any = {};
  activeIndices: number[] = [];
  activeNft: any[] = [];
  transactionModalData: ITransactionDetails;
  transactionLoader = false;
  searchControl = new FormControl();
  unsubscribeSearchControl = new Subject<void>();
  viewTransactionDetails: any;
  imageLoading: boolean = true;
  searchKeyword: string = '';
  private destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  /** Who the list was last loaded for; a change of wallet, user or custody mode reloads it. */
  private loadedForSession = '';
  @Input() isView: boolean = false;


  /**
   * Creates an instance of contract transactions component.
   * @param transactionService
   * @param webStorageService
   * @param toastr
   * @param commonService
   */
  constructor(
    private transactionService: TransactionService,
    private webStorageService: WebStorageService,
    private toastr: ToastrService,
    private commonService: CommonService,
    private accountService: AccountService,
    private clibboard: ClipboardService,
    private toast: ToastrService,
    private renderer: Renderer2,
    private heightService: HeightService
  ) {
    this.transactionModalData = this.getDefaultTransactionDetails();
  }

  /**
   * Gets default transaction details
   * @param [overrides]
   * @returns default transaction details
   */
  getDefaultTransactionDetails(overrides?: Partial<ITransactionDetails>): ITransactionDetails {
    const defaultDetails: ITransactionDetails = {
      activeNft: {},
      transactionHash: '',
      contractAddress: '',
      operationName: '',
      operationStatus: 0,
      transactionTime: '',
      errorMessage: '',
      nfts: [
        {
          _id: '',
          name: '',
          primary_media: '',
          collectionAddress: '',
          secondary_media: []
        }
      ]
    };

    return { ...defaultDetails, ...overrides };
  }


  /**
   * on init
   */
  ngOnInit(): void {
    this.loadForSession();

    // The panel now lives across page changes (AppRouteReuseStrategy keeps the layout), so it
    // no longer reloads on every click. It still picks up a wallet connected, a KYC completed
    // or a custody switch made since it last loaded, on the next page change.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadForSession());

    this.commonServiceSubscription = this.commonService.transactionListener.subscribe(response => {
      if (response.status && this.canLoadTransactionData()) {
        this.getTransactions('', 1, !response.status);
        if (response.count == 1) {
          this.updateViewTransactions();
        }

      }
    });

    this.handleSearchControl()
  }

  /**
   * Reads the session the rest of the app stored and loads the list for it, unless the list
   * is already loaded for exactly that session.
   */
  private loadForSession(): void {
    this.account = this.webStorageService.getLocalStorage('account') != null ? JSON.parse(this.webStorageService.getLocalStorage('account') || '') : this.account;
    this.user = this.webStorageService.getLocalStorage('user') != null ? JSON.parse(this.webStorageService.getLocalStorage('user') || 'undefined') : this.user;
    this.regulated = JSON.parse(this.webStorageService.getLocalStorage('regulated') || 'true');

    const canLoad = !!this.account?.chainId && this.canLoadTransactionData();
    const session = JSON.stringify([
      this.account?.walletAddress, this.account?.chainId, this.user?._id, this.regulated, canLoad,
    ]);
    if (session === this.loadedForSession) return;
    this.loadedForSession = session;

    if (canLoad) {
      this.transhUrl = getExplorerBaseUrl(this.account?.chainId);
      this.getTransactions();
      this.getViewTransactions();
    } else {
      this.transactions = [];
      this.viewTransactionDetails = undefined;
    }
  }

  /**
   * on destroy
   */
  ngOnDestroy(): void {
    if (this.commonServiceSubscription) {
      this.commonServiceSubscription.unsubscribe();
    }
  }

  /**
   * Haniles seach control
   */
 handleSearchControl(): void {
  this.searchControl.valueChanges
    .pipe(
      distinctUntilChanged(),
      debounceTime(1500),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((response) => {
      this.search(response);
    });
}


  /**transacation expand and collapse */
  showTransacation() {
    this.showtransactiondetail = !this.showtransactiondetail;
    if (this.canLoadTransactionData()) {
      this.updateViewTransactions(0);
    }
  }

  setTransaction(transaction: any) {
    this.transaction = transaction;
    this.transactionData = transaction
    this.transactionDetailsModal?.show();
  }

  public openTransactionDetailsPopup(transaction: any, activeNft: any): void {
    for (const nft of transaction.nft_id ?? []) {
      nft.fileType = fileTypeOf(nft);
    }
    this.transactionModalData = this.getDefaultTransactionDetails({
      // Not every logged call is about an asset — the deposit fee is paid before anything is
      // minted — so this stays an object the modal can read rather than becoming undefined.
      activeNft: activeNft || {},
      transactionHash: transaction.transaction_hash,
      contractAddress: transaction.to,
      operationName: transaction.transaction_name,
      operationStatus: transaction.status,
      transactionTime: transaction.createdAt,
      errorMessage: transaction.error_message,
      nfts: transaction.nft_id
    });
    console.log(this.transactionModalData);

    this.transactionData = transaction
    this.transactionDetailsModal?.show();
  }

  /**
   * Closes transaction modal
   */
  closeTransactionModal() {
    this.transactionModalData = this.getDefaultTransactionDetails();
    this.transactionDetailsModal?.hide()
  }
  totalDocs: number = 0
  currentPage: number = 1
  disableInfiniteScroll = false
  /**
   * Params contract transactions component
   * @param [keyword] 
   * @param [pageNo] 
   * @param [transactiodataState] 
   * STATE Param Condition: 
   * i).if the transactiodataState is false remove the current state and append the new values 
   * ii).By default transactiodataState is true so we can append the new values with the current values 
   */
  getTransactions(keyword?: string, pageNo: number = 1, transactiodataState: boolean = true, isVirtualScroll: boolean = false) {
    if (!this.canLoadTransactionData()) {
      this.transactionLoader = false;
      this.transactions = [];
      this.totalDocs = 0;
      this.currentPage = 1;
      this.disableInfiniteScroll = true;
      return;
    }

    this.transactionLoader = true;
    const pageQuery = pageNo ? `&page=${pageNo}&limit=${TRANSACTIONS_PAGE_LIMIT}` : '';
    this.transactionService.getTransactions(keyword, pageQuery).subscribe({
      next: (response: any) => {
        this.transactionLoader = false;
        this.disableInfiniteScroll = !response.data.hasNextPage
        this.transactions = (transactiodataState && isVirtualScroll == true) ? [...this.transactions, ...response.data.docs] : [...response.data.docs]
        this.transactions.forEach((transaction: { nft_id: any[]; }) => (transaction.nft_id ?? []).forEach((nft: any) => {
          nft.fileType = fileTypeOf(nft);
        }));
        this.totalDocs = response.data.totalDocs;
        this.currentPage = response.data.page;
        // Initialize active indices to 0 for each ngFor block
        // this.activeIndices = new Array(response?.data?.docs?.length).fill(0);
        // On infinite scroll keep the already-loaded rows' selections and add 0s for the
        // appended page; otherwise (first page / search / refresh) start fresh.
        const pageIndices = new Array(response?.data?.docs?.length ?? 0).fill(0);
        this.activeIndices = (transactiodataState && isVirtualScroll == true) ? [...this.activeIndices, ...pageIndices] : pageIndices;
        this.setActiveNft();
      },
      error: (error: any) => {
        this.transactionLoader = false;
        // this.toastr.error(error?.data?.message || "Something went wrong, try again later.");
      }
    })
  }

  /**
   * Sets active nft
   */
  setActiveNft(): void {
    this.transactions = this.transactions.map((nfts: { nft_id: any[]; }) => {
      return {
        ...nfts,
        // active_nft: (nfts.nft_id ?? [])[0]
        // Keep the asset already picked on rows loaded earlier (infinite scroll re-runs this over the whole list).
        active_nft: (nfts as any).active_nft ?? (nfts.nft_id ?? [])[0]
      };
    });
    this.transactions.map((tx: any) => {
      if (tx.active_nft) {
        tx.active_nft.fileType = fileTypeOf(tx.active_nft);
      }
      tx.thumbnail = this.thumbnailFor(tx);
    });
  }

  /**
   * The image shown against a transaction.
   *
   * Normally the active asset's own media. Some calls have no asset behind them at all — the
   * onboarding deposit fee is paid before anything is minted — and those fall back to an icon
   * for the operation rather than an empty frame.
   */
  private thumbnailFor(transaction: any): string {
    const nft = transaction?.active_nft;
    if (nft) {
      const media = nft.fileType === 'html'
        ? (nft.preview_image || nft.secondary_media?.[0])
        : nft.primary_media;
      return media || DEFAULT_TRANSACTION_THUMBNAIL;
    }

    return /deposit fee/i.test(transaction?.transaction_name || '')
      ? 'assets/images/use-deposit.svg'
      : DEFAULT_TRANSACTION_THUMBNAIL;
  }

  /**
   * Search with transaction details
   * @param keyword
   */
  search(keyword: string): void {
    this.searchKeyword = keyword;
    this.getTransactions(keyword);
  }

  /**
   * Sets active index
   * @param index
   */
  setActiveIndex(transactionIndex: number, nftIndex: number): void {
    this.activeIndices[transactionIndex] = nftIndex;
    this.transactions[transactionIndex].active_nft = this.transactions[transactionIndex].nft_id[nftIndex]
    this.transactions[transactionIndex].thumbnail = this.thumbnailFor(this.transactions[transactionIndex]);
  }

  /**
   * Gets view transactions
   */
  getViewTransactions() {
    if (!this.canLoadTransactionData()) {
      this.viewTransactionDetails = undefined;
      return;
    }

    this.transactionService.getViewTransactions().subscribe({
      next: (response: any) => {
        this.viewTransactionDetails = response['data'];
      },
      error: (error) => {
        if (this.accountService.shouldTreatUnauthorizedAsSessionExpiry(error)) {
          this.accountService.updateAuthentication(false);
          this.toastr.error("You’ve been logged out due to inactivity.");
        }
      }
    })
  }

  /**
   * Updates view transactions
   * @param {number} count
   */
  updateViewTransactions(count?: number) {
    if (!this.canLoadTransactionData() || !this.user?._id) {
      return;
    }

    let totalCount = 0;
    if (count != 0) {
      totalCount = this.viewTransactionDetails?.['count'] ? this.viewTransactionDetails?.['count'] + 1 : 1;
    }
    const data = {
      user_id: this.user._id,
      count: totalCount
    }
    this.transactionService.updateViewTransactions(data).subscribe((response) => {
      this.getViewTransactions();
    })

  }

  /**
   * Adjusts the tooltip size based on the content height.
   * 
   */
  public setTooltipSize() {
    (<HTMLElement>document.getElementById(`tooltip-title`)).classList.remove('add-content');
    const contentHeight = (<HTMLElement>document.getElementById(`tooltip-title`)).scrollHeight;
    (<HTMLElement>document.getElementById(`tooltip-title`)).classList.add('add-content');
    if (contentHeight > 59) {
      (<HTMLElement>document.getElementById(`tooltip-head`)).classList.add('tooltip-details');
    }
    else {
      (<HTMLElement>document.getElementById(`tooltip-head`)).classList.remove('tooltip-details')
    }
  }

  /**
   * Copies the provided address to the clipboard
   *
   * @param {string} address - The address to be copied to the clipboard.
   */
  copyAddress(address: string) {
    this.clibboard.copy(address);
    this.toast.success('Address copied successfully.');
  }

  ngAfterViewInit() {
    // const updateTop = () => {
    //   const h = this.heightService.getHeaderHeight();
    //   const normalOpen = !!document.querySelector('.navbar-collapse.show, .sidebar-collapse.show');
    //   const addOpen = !!document.querySelector('.navbar-collapse.add.show');
    //   let offset = 69;
    //   let filterOffset = 30;
  
    //   if (window.innerWidth <= 480) {
    //     offset = addOpen ? 100 : normalOpen ? 150 : 69;
    //   } else if (window.innerWidth <= 1199) {
    //     offset = addOpen ? 90 : normalOpen ? 120 : 69;
    //   }

    //    if (window.innerWidth <= 480) {
    //     filterOffset = addOpen ? 180 : normalOpen ? 220 : 30;
    //   } else if (window.innerWidth <= 1199) {
    //     filterOffset = addOpen ? 160 : normalOpen ? 190 : 30;
    //   }
  
    //   const el = document.querySelector('.transactiontopheight') as HTMLElement;
    //   const filterel = document.querySelector('.collapse-arrow') as HTMLElement;
    //   if (el) this.renderer.setStyle(el, 'top', `${h + offset}px`);
    //   if (filterel) this.renderer.setStyle(filterel, 'top', `${h + filterOffset}px`);
    // };
  
    // this.heightService.headerHeight$.subscribe(updateTop);
    // window.addEventListener('resize', updateTop);
  }
    
  
  
  /**
   * Keeps each card's DOM node tied to its transaction id. setActiveNft() rebuilds every
   * row object on each load, so without this ngFor re-created the whole list on every
   * infinite-scroll page, which collapsed the panel and jumped the scroll back to the top.
   */
  trackByTransaction(index: number, transaction: any) {
    return transaction?._id ?? index;
  }

  onScroll() {
    // Skip while a page is already loading or there is no next page, so fast
    // scrolling does not request (and append) the same page twice.
    if (this.transactionLoader || this.disableInfiniteScroll) return;
    const pageNo = this.currentPage + 1;
    // this.getTransactions(this.searchKeyword, pageNo, true);
    // isVirtualScroll = true so the next page is appended to the list instead of replacing it.
    this.getTransactions(this.searchKeyword, pageNo, true, true);
  }

  private canLoadTransactionData(): boolean {
    return canAccessVerifiedUserApis(this.user, this.regulated);
  }
}
